import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, MessageCircle, Eye, MoreHorizontal, Repeat2, MessageSquareQuote } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
// import { Skeleton } from '@/components/ui/skeleton';
import { formatDistanceToNow } from 'date-fns';
import { Loop, useLoopActions } from '@/hooks/use-loops';
import { cn } from '@/lib/utils';
import { getThumbnailUrl } from '@/utils/imageUtils';
import { getDarkGradient, getStringHash } from '@/utils/anonymousAvatars';
import LoopComments from './LoopComments';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useReblogActions, ReblogAnnotation } from '@/hooks/use-reblogs';
import TrailLensDialog from './TrailLensDialog';
import { useTopicWeights } from '@/hooks/use-topic-weights';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import VerifiedBadge from '@/components/common/VerifiedBadge';
import { useMatureContent } from '@/hooks/use-mature-content';
import { LinkifyContent } from '@/components/shared/LinkifyContent';

interface LoopFeedProps {
  loop: Loop;
  onEngage?: (loopId: string) => void;
  isEngaged?: boolean;
  className?: string;
  annotations?: ReblogAnnotation;
  showModerationDebug?: boolean;
}

const LoopFeed = ({ 
  loop, 
  onEngage, 
  isEngaged = false, 
  className,
  annotations,
  showModerationDebug = false
}: LoopFeedProps) => {
  const [showComments, setShowComments] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const { likeLoop, unlikeLoop, viewLoop, isLiking, isUnliking } = useLoopActions();
  const { reblog, quote, remix, isReblogging } = useReblogActions();
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteText, setQuoteText] = useState('');
  const [trailOpen, setTrailOpen] = useState(false);
  const { weights, upsertTopicWeights } = useTopicWeights();
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [actionsOpen, setActionsOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [isArtstream, setIsArtstream] = useState<boolean>(loop.is_artstream ?? false);
  const [showMature, setShowMature] = useState(false); // State to reveal mature content per-loop
  // Use the unified mature content hook (same as bulletins for consistency)
  const { showMatureContent } = useMatureContent();
  // Precompute anonymous background gradient; do not call hooks inside conditional JSX
  const anonBg = React.useMemo(() => getDarkGradient(getStringHash(loop.id || 'anonymous')), [loop.id]);
  
  // Check if mature content should be blurred (consistent with bulletins)
  const shouldBlurMature = loop.is_mature && !showMatureContent && !showMature;
  
  // Truncate content for display
  const TRUNCATE_LENGTH = 250;
  const shouldTruncate = (loop.content?.length || 0) > TRUNCATE_LENGTH;
  const displayContent = shouldTruncate && !isExpanded 
    ? loop.content?.slice(0, TRUNCATE_LENGTH) + '...'
    : loop.content;
  
  React.useEffect(() => {
    // Track view when feed item is rendered
    if (!loop.is_viewed) {
      viewLoop(loop.id);
    }
  }, [loop.id, loop.is_viewed, viewLoop]);
  
  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (loop.is_liked) {
      unlikeLoop(loop.id);
    } else {
      likeLoop(loop.id);
      // Treat like as an engagement signal to reveal author/results immediately
      onEngage?.(loop.id);
    }
  };
  
  // Only reveal author on like, not on click
  const handleEngage = () => {
    // Click doesn't reveal author anymore
    // We'll still call onEngage for any other functionality that might depend on it
    if (onEngage && !isEngaged) {
      onEngage(loop.id);
    }
  };
  
  const handleCommentsToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isEngaged) {
      setShowComments(!showComments);
    } else {
      handleEngage();
    }
  };

  const formatCount = (n: number | undefined) => {
    const v = n ?? 0;
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}m`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
    return String(v);
  };

  const createdAtDate = React.useMemo(() => {
    const d = new Date(loop.created_at);
    return isNaN(d.getTime()) ? null : d;
  }, [loop.created_at]);

  return (
    <div className={cn("relative bg-[#1A1A1B] shadow-md border border-[#343536] rounded-xl p-4 mb-4 text-[#D7DADC]", className)}>

      {/* Reloop banner */}
      {annotations?.rebloggedBy && (
        <div className="-mt-1 mb-2 text-xs text-[#818384] flex items-center gap-1">
          <Repeat2 size={14} className="text-[#818384]" />
          <span>
            Relooped by <span className="font-medium text-[#D7DADC]">@{annotations.rebloggedBy.username}</span>
          </span>
        </div>
      )}
      {/* Author info - show actual info if engaged, otherwise skeleton */}
      <AnimatePresence>
        {isEngaged ? (
          <motion.div
            initial={{ opacity: 0, height: 'auto' }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="flex items-center gap-3 mb-3"
          >
            <Avatar className="w-8 h-8">
              <AvatarImage src={(getThumbnailUrl(loop.user.profile_image_url || null, 80) || undefined) as string | undefined} />
              <AvatarFallback>
                {loop.user.display_name?.[0] || loop.user.username?.[0] || '?'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <div className="font-medium text-sm">
                {loop.user.display_name || loop.user.username}
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>{createdAtDate ? formatDistanceToNow(createdAtDate, { addSuffix: true }) : 'recently'}</span>
                {loop.primary_community && (
                  <>
                    <span className="text-[#9CA3AF]">→</span>
                    <span
                      className="leading-none text-sm font-medium"
                      style={{
                        backgroundImage: loop.primary_community.gradient || loop.primary_community.color 
                          ? `linear-gradient(90deg, ${loop.primary_community.color || '#A78BFA'}, ${loop.primary_community.color ? `${loop.primary_community.color}99` : '#C4B5FD'})`
                          : 'linear-gradient(90deg, #A78BFA, #C4B5FD)',
                        WebkitBackgroundClip: 'text',
                        backgroundClip: 'text',
                        color: 'transparent',
                      }}
                    >
                      #{loop.primary_community.name}
                    </span>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Eye size={12} />
              {loop.view_count}
            </div>
          </motion.div>
        ) : (
          <motion.div 
            className="flex items-center gap-3 mb-3" 
            initial={{ opacity: 0.7 }}
            animate={{ opacity: 0.8 }}
          >
            <div 
              className="w-8 h-8 rounded-full border border-border/40 flex items-center justify-center overflow-hidden shadow-sm"
              style={{ background: anonBg }}
            ></div>
            <div className="flex-1">
              <motion.div 
                className="relative overflow-hidden w-28 h-4 text-sm font-medium text-muted-foreground opacity-60 mb-1"
                initial={{ opacity: 0.4 }}
                animate={{
                  opacity: [0.4, 0.6, 0.4],
                  x: [-1, 1, -1],
                  transition: { repeat: Infinity, duration: 1.5 }
                }}
              >
                <span className="font-mono blur-[1.5px] tracking-widest">anonymous user</span>
              </motion.div>
              <div className="text-xs text-muted-foreground/50 blur-[1px]">
                recently posted
              </div>
            </div>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Eye size={12} />
              <span className="w-3 h-3 bg-foreground/20 rounded-full"></span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Loop content */}
      <div className="mb-3">
        <p className="leading-relaxed whitespace-pre-wrap text-base">
          <LinkifyContent 
            content={displayContent} 
            authorId={loop.user_id}
          />
        </p>
        {shouldTruncate && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="text-sm text-purple-400 hover:text-purple-300 font-medium mt-1 transition-colors"
          >
            {isExpanded ? 'Show less' : 'Read more'}
          </button>
        )}
      </div>
      
      {/* Media content */}
      {(loop.image_url || loop.video_url) && (
        <div className="mb-3 inline-block max-w-full rounded-md overflow-hidden bg-[#151516] relative">
          {loop.image_url ? (
            <img 
              src={loop.image_url} 
              alt="Loop media"
              className={cn(
                "block max-w-full h-auto object-contain max-h-[500px] border-0 outline-none ring-0 transition-all duration-200",
                shouldBlurMature && "blur-2xl"
              )}
            />
          ) : loop.video_url ? (
            <video 
              src={loop.video_url}
              poster={loop.thumbnail_url}
              className={cn(
                "block max-w-full h-auto object-contain max-h-[500px] border-0 outline-none ring-0 transition-all duration-200",
                shouldBlurMature && "blur-2xl"
              )}
              controls={!shouldBlurMature}
              playsInline
            />
          ) : null}
          
          {/* Mature content overlay */}
          {shouldBlurMature && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm">
              <div className="text-center space-y-3 p-4">
                <p className="text-white font-medium text-lg">Sensitive Content</p>
                <p className="text-gray-300 text-sm max-w-xs">This post may contain mature or NSFW content</p>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMature(true);
                  }}
                  variant="outline"
                  className="bg-white/10 hover:bg-white/20 border-white/20 text-white"
                >
                  Show Content
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Actions - show actual buttons if engaged, otherwise skeletons */}
      <div className="pt-2 border-t border-[#343536]">
        <AnimatePresence>
          {isEngaged ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="flex items-center justify-between"
            >
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    'p-2 h-auto rounded-full bg-[#272729] hover:bg-[#2D2D2F]',
                    loop.is_liked && 'text-[#FF4500] bg-[#3A2A24] hover:bg-[#3F2F29]'
                  )}
                  onClick={handleLike}
                  disabled={isLiking || isUnliking}
                >
                  <Heart 
                    size={18} 
                    className={loop.is_liked ? 'fill-current' : ''} 
                  />
                </Button>
                
                <Button
                  variant="ghost"
                  size="sm"
                  className="p-2 h-auto rounded-full bg-[#272729] hover:bg-[#2D2D2F]"
                  onClick={handleCommentsToggle}
                >
                  <MessageCircle size={18} />
                </Button>

                {/* Reblog actions */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="p-2 h-auto rounded-full bg-[#272729] hover:bg-[#2D2D2F]"
                  onClick={(e) => { e.stopPropagation(); reblog(loop.id); }}
                  disabled={isReblogging}
                >
                  <Repeat2 size={18} />
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  className="p-2 h-auto rounded-full bg-[#272729] hover:bg-[#2D2D2F]"
                  onClick={(e) => { e.stopPropagation(); setQuoteOpen(true); }}
                >
                  <MessageSquareQuote size={18} />
                </Button>

                {/* Remix removed */}
              </div>
              
              <Button variant="ghost" size="sm" className="p-2 h-auto rounded-full bg-[#272729] hover:bg-[#2D2D2F]" onClick={(e) => { e.stopPropagation(); setActionsOpen(true); }}>
                <MoreHorizontal size={18} />
              </Button>
            </motion.div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="sm"
                  className="p-2 h-auto rounded-full bg-[#272729] hover:bg-[#2D2D2F]"
                  onClick={handleLike}
                  disabled={isLiking || isUnliking}
                >
                  <Heart size={18} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="p-2 h-auto rounded-full opacity-50 cursor-not-allowed bg-[#272729]"
                  disabled={true}
                >
                  <MessageCircle size={18} className="text-[#818384]" />
                </Button>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                className="p-2 h-auto rounded-full opacity-50 cursor-not-allowed bg-[#272729]" 
                disabled={true}
              >
                <MoreHorizontal size={18} className="text-[#818384]" />
              </Button>
            </div>
          )}
        </AnimatePresence>
      </div>
      {isEngaged && loop.relevance_topic && (
        <div className="mt-2 text-xs text-[#818384] flex items-center gap-2">
          <span>
            Why this post: <span className="font-medium text-[#D7DADC]">#{loop.relevance_topic}</span>
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 bg-[#272729] hover:bg-[#2D2D2F] text-[#D7DADC]"
            onClick={(e) => {
              e.stopPropagation();
              const topic = loop.relevance_topic!;
              const current = new Map<string, number>((weights || []).map(w => [w.topic, w.weight]));
              const next = Math.max(0, (current.get(topic) ?? 0) - 0.1);
              upsertTopicWeights([{ topic, weight: next }]);
              // Let React Query refresh the feed naturally via the hook; no immediate removal
            }}
          >
            Less of #{loop.relevance_topic}
          </Button>
        </div>
      )}
      {isEngaged && (
        <>
          {/* Trail summary and counts */}
          <div className="mt-2 text-xs text-[#818384] flex items-center flex-wrap gap-x-3 gap-y-1">
            <span>Original by <span className="font-medium text-[#D7DADC] inline-flex items-center gap-1.5">
              @{loop.user.username}
              {loop.user.is_admin && (
                <VerifiedBadge type="admin" size={16} className="inline-block" />
              )}
              {loop.user.is_partner && (
                <VerifiedBadge type="partner" size={16} className="inline-block" />
              )}
            </span></span>
            <span><Heart size={12} className="inline mr-1 text-[#818384]" />{formatCount(loop.like_count)}</span>
            <span><MessageCircle size={12} className="inline mr-1 text-[#818384]" />{formatCount(loop.comment_count)}</span>
            <span>↻ {formatCount(loop.reblog_count)}</span>
            <span>✍ {formatCount(loop.quote_count)}</span>
            {/* Remix count removed */}
            <button className="underline hover:no-underline text-[#D7DADC]" onClick={(e) => { e.stopPropagation(); setTrailOpen(true); }}>View trail</button>
          </div>
          {/* Quote chips */}
          {annotations?.quoteChips?.length ? (
            <div className="mt-2 flex flex-wrap gap-2">
              {annotations.quoteChips.map((chip) => (
                <div key={chip.id} className="text-xs px-2 py-1 rounded-full border border-[#343536] bg-[#272729] max-w-[70%] truncate">
                  <span className="font-medium mr-1 text-[#D7DADC]">@{chip.user?.username || 'unknown'}</span>
                  <span className="text-[#818384]">{chip.content}</span>
                </div>
              ))}
              {annotations.quoteChips.length >= 2 && (
                <button className="text-xs underline text-[#D7DADC] hover:text-white" onClick={(e) => { e.stopPropagation(); setTrailOpen(true); }}>+ more</button>
              )}
            </div>
          ) : null}
          {showModerationDebug && loop.moderation_debug && (
            <div className="mt-3 text-xs text-[#e5e7eb] bg-[#312f40] border border-[#544f76] rounded-lg p-3 space-y-1">
              <div className="font-semibold text-sm text-[#f9fafb]">Moderation Debug</div>
              <div className="flex flex-wrap gap-4">
                <div>
                  <span className="text-[#a5b4fc] mr-1">Adult score:</span>
                  <span>{loop.moderation_debug.adultScore !== undefined ? loop.moderation_debug.adultScore.toFixed(2) : 'n/a'}</span>
                </div>
                <div>
                  <span className="text-[#a5b4fc] mr-1">Racy score:</span>
                  <span>{loop.moderation_debug.racyScore !== undefined ? loop.moderation_debug.racyScore.toFixed(2) : 'n/a'}</span>
                </div>
                <div>
                  <span className="text-[#a5b4fc] mr-1">Thresholds:</span>
                  <span>
                    adult ≥ {loop.moderation_debug.thresholds?.adult?.toFixed(2) ?? 'n/a'},
                    racy ≥ {loop.moderation_debug.thresholds?.racy?.toFixed(2) ?? 'n/a'}
                  </span>
                </div>
                <div>
                  <span className="text-[#a5b4fc] mr-1">Flagged:</span>
                  <span>{loop.is_mature ? 'Yes' : 'No'}</span>
                </div>
              </div>
              <div className="text-[#94a3b8] break-all">
                SafeSearch: {JSON.stringify(loop.moderation_debug, null, 0)}
              </div>
            </div>
          )}
        </>
      )}

      {/* Comments section */}
      <AnimatePresence>
        {showComments && isEngaged && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 pt-4 border-t"
          >
            <LoopComments loopId={loop.id} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Quote dialog */}
      <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Quote this post</DialogTitle>
          </DialogHeader>
          <Textarea
            placeholder="Add your thoughts..."
            value={quoteText}
            onChange={(e) => setQuoteText(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setQuoteOpen(false)}>Cancel</Button>
            <Button
              onClick={() => { quote(loop.id, quoteText.trim()); setQuoteText(''); setQuoteOpen(false); }}
              disabled={!quoteText.trim()}
            >
              Post Quote
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Actions dialog */}
      <Dialog open={actionsOpen} onOpenChange={setActionsOpen}>
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Post actions</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <Button variant="secondary" onClick={() => { setActionsOpen(false); setTrailOpen(true); }}>View trail</Button>
            {((user?.id && user.id === loop.user_id) || profile?.is_admin) && (
              <>
                <div className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2 bg-[#171718]">
                  <div className="flex items-start gap-2">
                    <Label className="text-sm">Is it art?</Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-xs text-muted-foreground underline cursor-help">What is this?</span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="max-w-xs text-sm">If enabled, this post will be featured in Artstream. Turn off for conversations or non-art posts.</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <Switch checked={isArtstream} onCheckedChange={setIsArtstream} />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <Button
                    variant="outline"
                    onClick={async () => {
                      try {
                        const { error } = await supabase
                          .from('loops')
                          .update({ is_artstream: isArtstream })
                          .eq('id', loop.id);
                        if (error) throw error;
                        toast({ title: 'Updated', description: isArtstream ? 'Post will appear in Artstream.' : 'Post removed from Artstream.' });
                        setActionsOpen(false);
                        await queryClient.invalidateQueries({ queryKey: ['loops'] });
                      } catch (err: any) {
                        console.error('[LoopFeed] update is_artstream failed', err);
                        toast({ title: 'Update failed', description: err?.message || 'Could not update post.', variant: 'destructive' });
                      }
                    }}
                  >
                    Save
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => { setActionsOpen(false); setConfirmDeleteOpen(true); }}
                  >
                    Delete post
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm delete */}
      <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Delete this post?</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground">This will remove the post from Loops. This action cannot be undone.</div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={async () => {
                try {
                  // Attempt hard delete; RLS should allow if owner. Admins may require server role; if it fails, show error.
                  const { error } = await supabase.from('loops').delete().eq('id', loop.id);
                  if (error) throw error;
                  toast({ title: 'Post deleted' });
                  setConfirmDeleteOpen(false);
                  await queryClient.invalidateQueries({ queryKey: ['loops'] });
                } catch (err: any) {
                  console.error('[LoopFeed] delete failed', err);
                  toast({ title: 'Delete failed', description: err?.message || 'You may not have permission to delete this post.', variant: 'destructive' });
                }
              }}
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Trail Lens dialog */}
      <TrailLensDialog postId={loop.id} open={trailOpen} onOpenChange={setTrailOpen} />
    </div>
  );
};

export default LoopFeed;